import { useEffect } from "react";
// import './App.css'
import styles from "./App.module.css";
import {
  useChatStore,
  useAuthStore,
  useUIStore,
  useMindspaceStore,
} from "@store";
import {
  getInitialWordPressData,
  InitialDataResponse,
} from "./services/authService";
import ChatBubble from "./components/chat/ChatBubble";
import ChatSidebarView from "./components/chat/ChatSidebarView";
import ConversationHistory from "./components/chat/ConversationHistory";
import ErrorMessageDisplay from "./components/common/ErrorMessageDisplay";
import ToastContainer from "./components/common/ToastContainer";
import { logError, logInfo, logDebug } from "./utils/logging";

// Function to load initial data, can be called on demand
export const initializeChatData = async (
  setLoadingInitialData: (loading: boolean) => void,
  setInitialData: (data: InitialDataResponse) => void,
  setInitialDataError: (error: string | null) => void,
) => {
  logInfo("Smartie AI: Attempting to load initial WordPress data...");
  setLoadingInitialData(true);
  try {
    const initialData = await getInitialWordPressData();
    logInfo("Smartie AI: Initial WordPress data received:", initialData);
    setInitialData(initialData);
  } catch (error) {
    logError("Smartie AI: Error fetching initial WordPress data:", error);
    const errorMessage =
      error instanceof Error ? error.message : "A apărut o eroare necunoscută";
    setInitialDataError(errorMessage);
  } finally {
    setLoadingInitialData(false); // Ensure loading is set to false in both cases
  }
};

function App() {
  const sessionId = useChatStore((state) => state.sessionId);
  const fetchAndLoadHistory = useChatStore(
    (state) => state.fetchAndLoadHistory,
  );
  // const initialDataLoaded = useAuthStore((state) => state.initialDataLoaded); // Removed unused
  const jwtPresent = useAuthStore((state) => !!state.jwt);

  // Select state and actions from useAuthStore individually for stable references
  // const setInitialData = useAuthStore((state) => state.setInitialData); // No longer directly called here
  // const setLoadingInitialData = useAuthStore((state) => state.setLoadingInitialData); // No longer directly called here
  // const setInitialDataError = useAuthStore((state) => state.setInitialDataError); // No longer directly called here
  const apiError = useAuthStore((state) => state.apiError);
  const isRateLimited = useAuthStore((state) => state.isRateLimited);
  const clearApiError = useAuthStore((state) => state.clearApiError);

  const chatViewMode = useUIStore((state) => state.chatViewMode);

  // Mindspace store for v2 API functionality
  const loadMindspaces = useMindspaceStore(
    (state) => state.loadMindspaces,
  );
  const mindspaceError = useMindspaceStore((state) => state.error);
  const isMindspaceLoading = useMindspaceStore((state) => state.isLoading);
  const isMindspaceInitialized = useMindspaceStore(
    (state) => state.isInitialized,
  );
  const clearMindspaceError = useMindspaceStore((state) => state.clearError);

  // Removed useEffect that automatically called loadInitialData on mount

  // Initialize mindspace when JWT is available (v2 API functionality)
  useEffect(() => {
    // Prevent infinite loop: don't retry if there's already an error
    // The store handles its own internal retry logic with backoff
    if (
      jwtPresent &&
      !isMindspaceInitialized &&
      !isMindspaceLoading &&
      !mindspaceError
    ) {
      logDebug(
        "App: JWT available, initializing mindspaces for v2 features...",
      );
      loadMindspaces().catch((error) => {
        logError("App: Failed to initialize mindspaces:", error);
      });
    }
  }, [
    jwtPresent,
    isMindspaceInitialized,
    isMindspaceLoading,
    mindspaceError,
    loadMindspaces,
  ]);

  useEffect(() => {
    // Only fetch history if initial data has been loaded (e.g., JWT is present)
    // and a session ID exists.
    if (jwtPresent && sessionId) {
      logDebug(
        `App: Initial data loaded, sessionId found (${sessionId}), fetching history...`,
      );
      fetchAndLoadHistory();
    }
  }, [jwtPresent, sessionId, fetchAndLoadHistory]);

  // useEffect for API error and rate limit alerts
  useEffect(() => {
    if (isRateLimited) {
      logInfo("Smartie AI: Rate limit is active.");
    } else if (apiError) {
      logInfo("Smartie AI: API Error encountered:", apiError);
    }
    if (apiError || isRateLimited) {
      const timer = setTimeout(() => {
        clearApiError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [apiError, isRateLimited, clearApiError]);

  // Construct className for the outer container
  const outerContainerClasses = [
    styles.chatViewContainerOuter,
    chatViewMode === "fullscreen"
      ? styles.chatViewContainerOuterFullScreen
      : chatViewMode === "sidebar"
        ? styles.chatViewContainerOuterSidebar
        : "", // No outer container when collapsed, or a specific collapsed style if needed
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {/* Global Toasts */}
      <ToastContainer />

      {/* Global Error Display Area */}
      {isRateLimited && (
        <ErrorMessageDisplay
          message="Ați atins limita de mesaje pentru moment. Vă rugăm să încercați mai târziu."
          type="warning"
          className={styles.globalError}
        />
      )}
      {apiError && !isRateLimited && (
        <ErrorMessageDisplay
          message={`A apărut o eroare API: ${apiError}`}
          type="error"
          className={styles.globalError}
        />
      )}
      {isMindspaceLoading && !isRateLimited && !apiError && (
        <ErrorMessageDisplay
          message="Se inițializează contextul aplicației..."
          type="info"
          className={styles.globalError}
        />
      )}
      {mindspaceError && !isMindspaceLoading && !isRateLimited && !apiError && (
        <ErrorMessageDisplay
          message={`Eroare la inițializarea contextului: ${mindspaceError}`}
          type="error"
          className={styles.globalError}
          action={{
            label: "Reîncearcă",
            onClick: () => {
              logDebug(
                "App: User clicked retry for mindspace initialization",
              );
              clearMindspaceError();
              loadMindspaces();
            },
          }}
        />
      )}

      {chatViewMode === "collapsed" && <ChatBubble />}
      {(chatViewMode === "sidebar" || chatViewMode === "fullscreen") && (
        <div className={outerContainerClasses}>
          {chatViewMode === "fullscreen" && <ConversationHistory />}
          <div className={styles.chatContentView}>
            <ChatSidebarView />
          </div>
        </div>
      )}
    </>
  );
}

export default App;
